import { createEffect, createMemo, createSignal, For, Show } from 'solid-js'
import { splitAlpha, withAlpha } from '../util/color.ts'

export const INK_PALETTE = [
  '#1d1d20',
  '#ffffff',
  '#868e96',
  '#e03131',
  '#f08c00',
  '#2f9e44',
  '#1971c2',
  '#9c36b5',
]

export const CANVAS_PALETTE = [
  '#f6f6f4',
  '#ffffff',
  '#fff9db',
  '#e7f5ff',
  '#343a40',
  '#1d1d20',
  '#000000',
]

interface Hsva {
  h: number
  s: number
  v: number
  a: number
}

function parseColor(input: string): Hsva | null {
  const raw = input.trim()
  const { rgb, alpha } = splitAlpha(raw.startsWith('#') ? raw : `#${raw}`)
  const m = /^#([0-9a-f]{6})$/i.exec(rgb)
  if (!m) return null
  const n = parseInt(m[1]!, 16)
  const r = (n >> 16) / 255
  const g = ((n >> 8) & 0xff) / 255
  const b = (n & 0xff) / 255
  const max = Math.max(r, g, b)
  const d = max - Math.min(r, g, b)
  let h = 0
  if (d > 0) {
    if (max === r) h = ((g - b) / d) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
  }
  return { h: (h * 60 + 360) % 360, s: max === 0 ? 0 : d / max, v: max, a: alpha }
}

function rgbHex({ h, s, v }: Hsva): string {
  const f = (k: number) => {
    const x = (k + h / 60) % 6
    return v - v * s * Math.max(0, Math.min(x, 4 - x, 1))
  }
  return (
    '#' +
    [f(5), f(3), f(1)]
      .map((c) => Math.round(c * 255).toString(16).padStart(2, '0'))
      .join('')
  )
}

const formatColor = (c: Hsva) => withAlpha(rgbHex(c), c.a)

const clamp01 = (x: number) => Math.min(1, Math.max(0, x))

/**
 * Track a drag on an element through pointer capture, reporting the pointer as
 * 0..1 fractions of the element's box.
 */
function dragArea(
  onDrag: (fx: number, fy: number) => void,
  onDone: () => void,
): (e: PointerEvent) => void {
  return (e) => {
    const el = e.currentTarget as HTMLElement
    el.setPointerCapture(e.pointerId)
    e.preventDefault()
    const report = (ev: PointerEvent) => {
      const r = el.getBoundingClientRect()
      onDrag(clamp01((ev.clientX - r.left) / r.width), clamp01((ev.clientY - r.top) / r.height))
    }
    const up = () => {
      el.removeEventListener('pointermove', report)
      el.removeEventListener('pointerup', up)
      el.removeEventListener('pointercancel', up)
      onDone()
    }
    el.addEventListener('pointermove', report)
    el.addEventListener('pointerup', up)
    el.addEventListener('pointercancel', up)
    report(e)
  }
}

interface Rgb {
  r: number
  g: number
  b: number
}

function hsvToRgb(c: Hsva): Rgb {
  const n = parseInt(rgbHex(c).slice(1), 16)
  return { r: n >> 16, g: (n >> 8) & 0xff, b: n & 0xff }
}

function rgbToHsva({ r, g, b }: Rgb, a: number): Hsva {
  const hex = '#' + [r, g, b].map((x) => Math.round(x).toString(16).padStart(2, '0')).join('')
  return { ...parseColor(hex)!, a }
}

interface Channel {
  label: string
  max: number
  get: (c: Hsva) => number
  set: (c: Hsva, value: number) => Hsva
  gradient: (c: Hsva) => string
}

const css = (c: Hsva) => rgbHex(c)

// H, S and V bars ignore the current s and v, so they stay readable at black,
// white and grays, where those would flatten every gradient to one color.
const pureHue = (c: Hsva) => css({ h: c.h, s: 1, v: 1, a: 1 })
const HUE_GRADIENT = `linear-gradient(to right, ${[0, 60, 120, 180, 240, 300, 359.9]
  .map((h) => css({ h, s: 1, v: 1, a: 1 }))
  .join(', ')})`

/** Keep the hue when a change through RGB lands on gray, where hue is undefined. */
function viaRgb(c: Hsva, patch: Partial<Rgb>): Hsva {
  const next = rgbToHsva({ ...hsvToRgb(c), ...patch }, c.a)
  return next.s === 0 || next.v === 0 ? { ...next, h: c.h } : next
}

const rgbChannel = (key: keyof Rgb, label: string): Channel => ({
  label,
  max: 255,
  get: (c) => hsvToRgb(c)[key],
  set: (c, v) => viaRgb(c, { [key]: v }),
  gradient: (c) => {
    const rgb = hsvToRgb(c)
    const at = (v: number) => `rgb(${key === 'r' ? v : rgb.r} ${key === 'g' ? v : rgb.g} ${key === 'b' ? v : rgb.b})`
    return `linear-gradient(to right, ${at(0)}, ${at(255)})`
  },
})

const CHANNELS: Channel[] = [
  {
    label: 'H',
    max: 360,
    get: (c) => c.h,
    set: (c, v) => ({ ...c, h: Math.min(v, 359.9) }),
    gradient: () => HUE_GRADIENT,
  },
  {
    label: 'S',
    max: 100,
    get: (c) => c.s * 100,
    set: (c, v) => ({ ...c, s: v / 100 }),
    gradient: (c) => `linear-gradient(to right, #fff, ${pureHue(c)})`,
  },
  {
    label: 'V',
    max: 100,
    get: (c) => c.v * 100,
    set: (c, v) => ({ ...c, v: v / 100 }),
    gradient: (c) => `linear-gradient(to right, #000, ${pureHue(c)})`,
  },
  rgbChannel('r', 'R'),
  rgbChannel('g', 'G'),
  rgbChannel('b', 'B'),
  {
    label: 'A',
    max: 255,
    get: (c) => c.a * 255,
    set: (c, v) => ({ ...c, a: v / 255 }),
    gradient: (c) => `linear-gradient(to right, transparent, ${css(c)})`,
  },
]

function InlinePicker(props: {
  value: string
  onInput: (color: string) => void
  onChange: (color: string) => void
}) {
  // Hue is kept locally: it is lost in the hex round-trip whenever s or v is 0.
  const [hsv, setHsv] = createSignal<Hsva>(parseColor(props.value) ?? { h: 0, s: 0, v: 0, a: 1 })
  const [hex, setHex] = createSignal(props.value)

  createEffect(() => {
    const v = props.value
    setHex(v)
    if (formatColor(hsv()) === v.toLowerCase()) return
    const next = parseColor(v)
    if (next) setHsv((cur) => (next.s === 0 || next.v === 0 ? { ...next, h: cur.h } : next))
  })

  const emit = (next: Hsva) => {
    setHsv(next)
    props.onInput(formatColor(next))
  }
  const finish = () => props.onChange(formatColor(hsv()))

  const commitHex = () => {
    const next = parseColor(hex())
    if (!next) {
      setHex(props.value)
      return
    }
    setHsv(next)
    props.onChange(formatColor(next))
  }

  return (
    <div class="picker">
      <For each={CHANNELS}>
        {(ch) => (
          <div class="picker-channel">
            <span>{ch.label}</span>
            <div
              class="picker-bar"
              classList={{ checker: ch.label === 'A' }}
              onPointerDown={dragArea((fx) => emit(ch.set(hsv(), fx * ch.max)), finish)}
            >
              <div class="picker-bar-fill" style={{ background: ch.gradient(hsv()) }} />
              <div
                class="picker-thumb"
                style={{ left: `${(ch.get(hsv()) / ch.max) * 100}%`, top: '50%' }}
              />
            </div>
            <input
              type="number"
              min="0"
              max={ch.max}
              value={Math.round(ch.get(hsv()))}
              onChange={(e) => {
                const v = Math.min(ch.max, Math.max(0, e.currentTarget.valueAsNumber || 0))
                const next = ch.set(hsv(), v)
                setHsv(next)
                props.onChange(formatColor(next))
              }}
            />
          </div>
        )}
      </For>
      <div class="picker-channel">
        <span>#</span>
        <input
          class="picker-hex"
          value={hex()}
          spellcheck={false}
          onInput={(e) => setHex(e.currentTarget.value)}
          onChange={commitHex}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        />
      </div>
    </div>
  )
}

/** One color property the style bar edits: the canvas, a fill, a stroke. */
export interface ColorTarget {
  key: string
  label: string
  readonly value: string
  palette: string[]
  /** Live while dragging in the picker. */
  onInput: (color: string) => void
  /** Once per finished change; also called alone for a swatch click. */
  onChange: (color: string) => void
}

const [chosenKey, setChosenKey] = createSignal<string | null>(null)

/**
 * A tab per color target, each showing its current color, above one set of
 * channel gradients and a palette that edit whichever tab is chosen. Always in
 * view: the native picker is a popup that holds the pointer until dismissed.
 */
export function ColorTabs(props: { targets: ColorTarget[] }) {
  // The chosen tab survives selection changes that keep it in view.
  const active = createMemo(
    () => props.targets.find((t) => t.key === chosenKey()) ?? props.targets[0] ?? null,
  )

  return (
    <Show when={active()} keyed>
      {(target) => {
        const current = () => splitAlpha(target.value)
        return (
          <div class="color-tabs">
            <div class="picker-tabs">
              <For each={props.targets}>
                {(t) => (
                  <button
                    type="button"
                    class="picker-tab"
                    classList={{ active: t.key === target.key }}
                    onClick={() => setChosenKey(t.key)}
                  >
                    <span class="picker-tab-swatch checker">
                      <span style={{ background: t.value }} />
                    </span>
                    {t.label}
                  </button>
                )}
              </For>
            </div>

            <div class="color-body">
              <InlinePicker
                value={target.value}
                onInput={target.onInput}
                onChange={target.onChange}
              />
              <div class="swatches">
                <For each={target.palette}>
                  {(color) => (
                    <button
                      type="button"
                      class="swatch"
                      classList={{ active: current().rgb === color }}
                      style={{ background: color }}
                      title={color}
                      // Keeps the current opacity, so picking a hue does not reset it.
                      onClick={() => target.onChange(withAlpha(color, current().alpha))}
                    />
                  )}
                </For>
              </div>
            </div>
          </div>
        )
      }}
    </Show>
  )
}
